import React, {
  ReactElement,
  useCallback,
  useState,
  useRef,
  useEffect,
} from 'react';
import type { CSSProperties } from 'react';
import cx from 'classnames';
import { useResizeDetector } from 'react-resize-detector';

import { SwitchButtonItem } from './SwitchButtonItem/SwitchButtonItem';
import type { SwitchButtonStyles } from './SwitchButton.constant';

import styles from './SwitchButton.module.sass';

export interface DataSwitchButton {
  value: string;
  position: string;
}

export interface OptionSwitch {
  label: string | ReactElement;
  value: string;
}

export interface SwitchButtonProps {
  currentValue: string;
  options: OptionSwitch[];
  activeSlide?: boolean;
  className?: string;
  theme?: SwitchButtonStyles;
  onChange: (value: string) => void;
}

interface SliderDimensionProps {
  left: number;
  width: number;
}

const SwitchButton = ({
  currentValue,
  options,
  activeSlide = true,
  className,
  theme,
  onChange,
}: SwitchButtonProps) => {
  const stylesDefault: SwitchButtonStyles = {
    ...theme,
  };

  const cssStyles = {
    '--height': stylesDefault.height,
    '--height-slider': stylesDefault.heightSlider,
    '--width-slider': stylesDefault.widthSlider,
    '--padding-slider': stylesDefault.paddingSlider,
    '--width-item': stylesDefault.widthItem,
    '--gap-item': stylesDefault.gapItem,
    '--background': stylesDefault.background,
    '--background-hover': stylesDefault.backgroundHover,
    '--background-slider': stylesDefault.backgroundSlider,
    '--border-radius': stylesDefault.borderRadius,
    '--color': stylesDefault.color,
    '--color-hover': stylesDefault.colorHover,
    '--color-active': stylesDefault.colorActive,
    '--color-active-hover': stylesDefault.colorActiveHover,
  } as CSSProperties;

  const classes = cx(styles['switch-button'], className);

  const switchParentRef = useRef<HTMLDivElement>(null);
  const buttonRefList = useRef<HTMLButtonElement[]>([]);
  const buttons = buttonRefList.current;
  const selectedButtonIdx = buttons.findIndex(
    (item) => item.value === currentValue,
  );

  useEffect(() => {
    buttonRefList.current = options.map(
      (_opt, i) => buttonRefList.current[i] || null,
    );
  }, [options]);

  const isLastButtonSelected = selectedButtonIdx === buttons.length - 1;

  const [sliderDimension, setSliderDimension] = useState<SliderDimensionProps>({
    left: 0,
    width: 0,
  });

  const getSliderDimension = useCallback(() => {
    const selectedIndex = options.findIndex(
      (option) => option.value === currentValue,
    );
    const selectedItem = buttons[selectedIndex];

    if (selectedItem) {
      setSliderDimension({
        left: selectedItem.offsetLeft,
        width: selectedItem.clientWidth,
      });
    }
  }, [currentValue, options, buttons]);

  useResizeDetector({
    targetRef: switchParentRef,
    onResize: getSliderDimension,
    handleWidth: true,
  });

  useEffect(() => getSliderDimension(), [getSliderDimension]);

  const handleChange = useCallback(
    (index: number) => {
      const currentRef = buttons[index];
      onChange(currentRef.value);

      // focus next button after selected or first
      if (index === buttons.length - 1) {
        buttons[0].focus();
        return;
      }
      buttons[index + 1].focus();
    },
    [onChange, buttons],
  );

  const getTabindex = useCallback(
    (index: number): number => {
      if (isLastButtonSelected && index === 0) {
        return 0;
      }
      if (!isLastButtonSelected && index === selectedButtonIdx + 1) {
        return 0;
      }
      return -1;
    },
    [isLastButtonSelected, selectedButtonIdx],
  );

  return (
    <div className={classes} style={cssStyles} ref={switchParentRef}>
      {options.map((option, index) => {
        const changeRef = (ref: HTMLButtonElement) => {
          buttons[index] = ref;
        };

        return (
          <SwitchButtonItem
            key={option.value}
            index={index}
            ref={changeRef}
            label={option.label}
            value={option.value}
            active={currentValue === option.value}
            tabIndex={getTabindex(index)}
            onClick={handleChange}
          />
        );
      })}
      {activeSlide && (
        <div
          className={styles['switch-button__slider']}
          style={sliderDimension}
        />
      )}
    </div>
  );
};

export default SwitchButton;
